
import React, { useEffect } from 'react';
import { Check, X, Crown, Zap, Sparkles } from 'lucide-react';
import { PRICING } from '@/constants/pricing';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';

const FeatureComparison = () => {
  const { isPremium, createCheckout } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent('view_pricing');
  }, [trackEvent]);

  const features = [
    {
      name: 'Número de imágenes',
      free: '10 imágenes/día (con registro)',
      premium: 'Ilimitadas',
      icon: '📸'
    },
    {
      name: 'Formatos soportados',
      free: 'JPEG, PNG, WebP',
      premium: 'JPEG, PNG, WebP',
      icon: '🎨'
    },
    {
      name: 'Tamaño máximo',
      free: '10MB por imagen',
      premium: '50MB por imagen',
      icon: '📏'
    },
    {
      name: 'Calidad máxima',
      free: 'Hasta 90%',
      premium: 'Hasta 95% (Ultra)',
      icon: '✨'
    },
    {
      name: 'Resolución máxima',
      free: '1920x1080px',
      premium: '4K (4096x2160px)',
      icon: '🖼️'
    },
    {
      name: 'Anuncios',
      free: 'Con anuncios',
      premium: 'Sin anuncios',
      icon: '🚫'
    },
    {
      name: 'Velocidad de procesamiento',
      free: 'Estándar',
      premium: 'Prioritario',
      icon: '⚡'
    },
    {
      name: 'Historial de compresiones',
      free: 'No disponible',
      premium: 'Historial completo',
      icon: '📋'
    }
  ];

  return (
    <div className="mt-8 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8 shadow-md">
      <div className="text-center mb-6">
        <Badge className="mb-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 px-4 py-1">
          <Sparkles className="w-3 h-3 mr-1" />
          Mejora tu experiencia
        </Badge>
        <h2 className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
          <Crown className="w-7 h-7 text-purple-500" />
          Comparación de Planes
        </h2>
        <p className="text-muted-foreground">
          Elige el plan que mejor se adapte a tus necesidades
        </p>
      </div>

      <Separator className="mb-6 bg-purple-200" />

      <div className="grid md:grid-cols-3 gap-6">
        {/* Plan Gratuito */}
        <Card className={`relative ${!isPremium ? 'ring-2 ring-blue-200 bg-blue-50' : ''}`}>
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Zap className="w-5 h-5 text-blue-500" />
              Plan Gratuito
            </CardTitle>
            {!isPremium && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Plan Actual
                </span>
              </div>
            )}
            <div className="text-3xl font-bold text-gray-700">
              $0<span className="text-lg text-gray-500">/mes</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{feature.icon}</span>
                  <span className="text-sm font-medium text-gray-700">
                    {feature.name}
                  </span>
                </div>
                <span className="text-sm text-gray-600">
                  {feature.free}
                </span>
              </div>
            ))}
            
            <div className="pt-4">
              {!user ? (
                <Button 
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Comenzar Gratis
                </Button>
              ) : !isPremium ? (
                <Button 
                  variant="outline"
                  disabled
                  className="w-full border-gray-300 text-gray-500 bg-gray-50"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Plan Actual
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Plan Premium Mensual */}
        <Card className={`relative transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${isPremium ? 'ring-2 ring-purple-200 bg-purple-50' : 'ring-2 ring-purple-300'}`}>
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Crown className="w-5 h-5 text-purple-500" />
              Plan Mensual
            </CardTitle>
            {isPremium && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Plan Actual
                </span>
              </div>
            )}
            <div className="text-3xl font-bold text-purple-700">
              ${PRICING.MONTHLY.price}<span className="text-lg text-purple-500">/{PRICING.MONTHLY.period}</span>
            </div>
            <p className="text-xs text-purple-600">{PRICING.MONTHLY.currency}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{feature.icon}</span>
                  <span className="text-sm font-medium text-gray-700">
                    {feature.name}
                  </span>
                </div>
                <span className="text-sm text-purple-600 font-medium">
                  {feature.premium}
                </span>
              </div>
            ))}
            
            {!isPremium && (
              <div className="pt-4">
                <Button 
                  onClick={() => {
                    trackEvent('click_subscribe', { plan: 'monthly' });
                    if (!user) {
                      navigate('/auth');
                      return;
                    }
                    createCheckout('monthly');
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {!user ? 'Iniciar Sesión para Comprar' : 'Elegir Mensual'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Premium Anual */}
        <Card className="relative ring-2 ring-green-300 bg-green-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Crown className="w-5 h-5 text-green-600" />
              Plan Anual
            </CardTitle>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                Ahorra 16%
              </span>
            </div>
            <div className="text-3xl font-bold text-green-700">
              ${PRICING.ANNUAL.price}<span className="text-lg text-green-500">/{PRICING.ANNUAL.period}</span>
            </div>
            <p className="text-xs text-green-600">{PRICING.ANNUAL.currency} (equivale a ${PRICING.ANNUAL.monthlyEquivalent}/{PRICING.MONTHLY.period})</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{feature.icon}</span>
                  <span className="text-sm font-medium text-gray-700">
                    {feature.name}
                  </span>
                </div>
                <span className="text-sm text-green-600 font-medium">
                  {feature.premium}
                </span>
              </div>
            ))}
            
            {!isPremium && (
              <div className="pt-4">
                <Button 
                  onClick={() => {
                    trackEvent('click_subscribe', { plan: 'annual' });
                    if (!user) {
                      navigate('/auth');
                      return;
                    }
                    createCheckout('annual');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {!user ? 'Iniciar Sesión para Comprar' : 'Elegir Anual'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-6">
        🔒 Prueba Premium sin compromiso — cancela cuando quieras
      </p>
    </div>
  );
};

export default FeatureComparison;
